import { Component, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { Task } from '@app/_components/task';
import { TaskService } from '@app/_components/task';
import { AuthenticationService, UserService, AlertService } from '@app/_services';
import * as moment from 'moment';
import { MatDialog } from '@angular/material/dialog';
import { BaseTaskViewComponent } from '../base-task-view.component';
import { first } from 'rxjs/operators';
import { Location } from '@angular/common';

export enum CalendarViewMode {
  DUE_DATE = 'dueDate',
  COMPLETED_DATE = 'completedDate',
  CREATED_DATE = 'createdDate',
  FOLLOWUP_DATE = 'followupDate'
}

@Component({
    selector: 'app-task-calendar-view',
    templateUrl: './task-calendar-view.component.html',
    styleUrls: ['./task-calendar-view.component.css'],
    standalone: false
})
export class TaskCalendarViewComponent extends BaseTaskViewComponent {
  // Calendar state
  currentDate: moment.Moment;
  currentMonth: moment.Moment;
  calendarViewMode: CalendarViewMode = CalendarViewMode.DUE_DATE;
  viewModeOptions = [
    { label: 'Due Date', value: CalendarViewMode.DUE_DATE },
    { label: 'Completed Date', value: CalendarViewMode.COMPLETED_DATE },
    { label: 'Created Date', value: CalendarViewMode.CREATED_DATE },
    { label: 'Followup Date', value: CalendarViewMode.FOLLOWUP_DATE }
  ];

  // Calendar data
  weeks: any[] = [];
  tasksByDate: Map<string, Task[]> = new Map();
  expandedDates: Set<string> = new Set(); // Track which dates are expanded
  loading: boolean = false; // Loading state for tasks
  
  // Analytics based on loaded tasks
  @Output() analyticsUpdate = new EventEmitter<any>();
  viewAnalytics: any = {};

  // Context menu
  items: any[] = [];

  constructor(
    authenticationService: AuthenticationService,
    taskService: TaskService,
    userService: UserService,
    alertService: AlertService,
    dialog: MatDialog,
    location: Location
  ) {
    super(authenticationService, taskService, userService, alertService, dialog, location);
    this.currentDate = moment();
    this.currentMonth = moment().startOf('month');
  }

  protected onInitView(): void {
    this.buildCalendar();
    this.loadTasksForMonth();
    this.initializeContextMenu();
  }

  private initializeContextMenu(): void {
    this.items = [
      {
        label: 'Copy Task',
        icon: 'pi pi-fw pi-copy',
        command: (event) => this.openTaskForClone()
      },
      {
        label: 'Sub Task',
        icon: 'pi pi-fw pi-clone',
        command: (event) => this.openTaskForSubTask()
      },
      {
        label: 'Mail Task',
        icon: 'pi pi-fw pi-envelope',
        command: (event) => this.openMailTask()
      },
      {
        label: 'Summary',
        icon: 'pi pi-fw pi-search',
        command: (event) => this.openSummary()
      },
      {
        label: 'Status Update req',
        icon: 'pi pi-question',
        command: (event) => this.sendStatusUpdateReq(this.selectedTask)
      },
    ];
  }

  protected onViewChanges(changes: SimpleChanges): void {
    // Calendar view always loads its own data month-wise from API
    // If filters, taskTag, or groupBy changed, reload tasks for current month
    if (changes['filters'] || changes['taskTag'] || changes['groupBy']) {
      this.loadTasksForMonth();
    }
    // Ignore external tasks input - calendar loads its own data independently
  }

  buildCalendar() {
    const startOfMonth = this.currentMonth.clone().startOf('month');
    const endOfMonth = this.currentMonth.clone().endOf('month');
    const startDate = startOfMonth.clone().startOf('week');
    const endDate = endOfMonth.clone().endOf('week');

    this.weeks = [];
    let currentWeek: any[] = [];
    let currentDay = startDate.clone();

    while (currentDay.isSameOrBefore(endDate, 'day')) {
      const tasks = this.getTasksForDate(currentDay);
      currentWeek.push({
        date: currentDay.clone(),
        isCurrentMonth: currentDay.isSame(this.currentMonth, 'month'),
        isToday: currentDay.isSame(moment(), 'day'),
        tasks: tasks
      });

      if (currentDay.day() === 6) { // Saturday
        // Check if week has any tasks
        const hasTasks = currentWeek.some(day => day.tasks && day.tasks.length > 0);
        this.weeks.push({ days: currentWeek, hasTasks: hasTasks });
        currentWeek = [];
      }

      currentDay.add(1, 'day');
    }

    if (currentWeek.length > 0) {
      // Check if week has any tasks
      const hasTasks = currentWeek.some(day => day.tasks && day.tasks.length > 0);
      this.weeks.push({ days: currentWeek, hasTasks: hasTasks });
    }
  }

  getTasksForDate(date: moment.Moment): Task[] {
    const dateKey = date.format('YYYY-MM-DD');
    return this.tasksByDate.get(dateKey) || [];
  }

  /**
   * Get visible tasks for a date (2 if not expanded, all if expanded)
   */
  getVisibleTasksForDate(date: moment.Moment): Task[] {
    const tasks = this.getTasksForDate(date);
    const dateKey = date.format('YYYY-MM-DD');
    if (this.isDateExpanded(dateKey) || tasks.length <= 2) {
      return tasks;
    }
    return tasks.slice(0, 2);
  }

  /**
   * Check if a date is expanded
   */
  isDateExpanded(dateKey: string): boolean {
    return this.expandedDates.has(dateKey);
  }

  /**
   * Get the number of hidden tasks for a date
   */
  getHiddenTasksCount(date: moment.Moment): number {
    const tasks = this.getTasksForDate(date);
    return Math.max(0, tasks.length - 2);
  }

  groupTasksByDate() {
    this.tasksByDate.clear();
    
    if (!this.tasks || this.tasks.length === 0) {
      this.buildCalendar();
      return;
    }
    
    // Apply filters first
    let filteredTasks = this.applyFilters(this.tasks);
    
    // Flatten tasks including children and remove duplicates by taskId
    const allTasks: Task[] = [];
    const taskIdSet = new Set<number>();
    
    filteredTasks.forEach(task => {
      // Add parent task if not already added
      if (task.taskId && !taskIdSet.has(task.taskId)) {
        allTasks.push(task);
        taskIdSet.add(task.taskId);
      }
      // Add children if they exist and haven't been added
      if (task.children && task.children.length > 0) {
        task.children.forEach(child => {
          if (child.taskId && !taskIdSet.has(child.taskId)) {
            allTasks.push(child);
            taskIdSet.add(child.taskId);
          }
        });
      }
    });

    allTasks.forEach(task => {
      let date: moment.Moment = null;
      
      switch (this.calendarViewMode) {
        case CalendarViewMode.DUE_DATE:
          date = task.dateDue ? moment(task.dateDue) : null;
          break;
        case CalendarViewMode.COMPLETED_DATE:
          date = (task as any).dateCompleted ? moment((task as any).dateCompleted) : null;
          break;
        case CalendarViewMode.CREATED_DATE:
          date = (task as any).dateCreated ? moment((task as any).dateCreated) : null;
          break;
        case CalendarViewMode.FOLLOWUP_DATE:
          date = (task as any).dateToFollowup ? moment((task as any).dateToFollowup) : null;
          break;
      }

      if (date && date.isValid()) {
        const dateKey = date.format('YYYY-MM-DD');
        if (!this.tasksByDate.has(dateKey)) {
          this.tasksByDate.set(dateKey, []);
        }
        // Check if task is already in the array for this date to avoid duplicates
        const tasksForDate = this.tasksByDate.get(dateKey);
        if (!tasksForDate.find(t => t.taskId === task.taskId)) {
          tasksForDate.push(task);
        }
      }
    });

    this.buildCalendar();
  }

  onViewModeChange() {
    // Clear expanded dates when view mode changes
    this.expandedDates.clear();
    this.loadTasksForMonth();
  }

  previousMonth() {
    // Clear expanded dates when month changes
    this.expandedDates.clear();
    this.currentMonth.subtract(1, 'month');
    this.loadTasksForMonth();
  }

  nextMonth() {
    // Clear expanded dates when month changes
    this.expandedDates.clear();
    this.currentMonth.add(1, 'month');
    this.loadTasksForMonth();
  }

  goToToday() {
    // Clear expanded dates when going to today
    this.expandedDates.clear();
    this.currentMonth = moment().startOf('month');
    this.loadTasksForMonth();
  }

  /**
   * Load tasks for the current month based on calendar view mode
   */
  private loadTasksForMonth(): void {
    if (this.loading) {
      return;
    }

    this.loading = true;

    // Calculate date range for the current month
    // Include a bit of padding to show tasks from adjacent weeks
    const startOfMonth = this.currentMonth.clone().startOf('month').startOf('week');
    const endOfMonth = this.currentMonth.clone().endOf('month').endOf('week');

    // Determine which date field to filter based on calendar view mode
    let dateField = 'dateDue';
    switch (this.calendarViewMode) {
      case CalendarViewMode.DUE_DATE:
        dateField = 'dateDue';
        break;
      case CalendarViewMode.COMPLETED_DATE:
        dateField = 'dateCompleted';
        break;
      case CalendarViewMode.CREATED_DATE:
        dateField = 'dateCreated';
        break;
      case CalendarViewMode.FOLLOWUP_DATE:
        dateField = 'dateToFollowup';
        break;
    }

    // Prepare filters for API call
    const apiFilters: any = {};
    
    // Convert filters to API format
    if (this.filters) {
      if (this.filters.taskId && Array.isArray(this.filters.taskId)) {
        apiFilters.taskId = { value: this.filters.taskId, matchMode: 'in' };
      }
      if (this.filters.name) {
        apiFilters.name = { value: this.filters.name, matchMode: 'contains' };
      }
      if (this.filters.status && Array.isArray(this.filters.status) && this.filters.status.length > 0) {
        apiFilters.status = { value: this.filters.status, matchMode: 'in' };
      }
      if (this.filters.priority && Array.isArray(this.filters.priority) && this.filters.priority.length > 0) {
        apiFilters.priority = { value: this.filters.priority, matchMode: 'in' };
      }
      if (this.filters.assignedTo) {
        if (Array.isArray(this.filters.assignedTo)) {
          const userIds = this.filters.assignedTo.map(u => u.userId || u);
          apiFilters['assignedToUserVO.userId'] = { value: userIds, matchMode: 'in' };
        } else {
          apiFilters['assignedToUserVO.userId'] = { value: [this.filters.assignedTo.userId], matchMode: 'in' };
        }
      }
      if (this.filters.createdBy) {
        if (Array.isArray(this.filters.createdBy)) {
          const userIds = this.filters.createdBy.map(u => u.userId || u);
          apiFilters['createdByUserVO.userId'] = { value: userIds, matchMode: 'in' };
        } else {
          apiFilters['createdByUserVO.userId'] = { value: [this.filters.createdBy.userId], matchMode: 'in' };
        }
      }
      if (this.filters.requestedBy) {
        if (Array.isArray(this.filters.requestedBy)) {
          const userIds = this.filters.requestedBy.map(u => u.userId || u);
          apiFilters['requestedByUserVO.userId'] = { value: userIds, matchMode: 'in' };
        } else {
          apiFilters['requestedByUserVO.userId'] = { value: [this.filters.requestedBy.userId], matchMode: 'in' };
        }
      }
    }

    // Add taskTag filter if present
    if (this.taskTag) {
      apiFilters.tagList = { value: this.taskTag, matchMode: 'in' };
    }

    // Create date range filter based on calendar view mode
    // Format: YYYY-MM-DD for API
    const dateRange = [
      startOfMonth.format('YYYY-MM-DD'),
      endOfMonth.format('YYYY-MM-DD')
    ];

    // Default sort
    const multiSortMeta = [
      { field: dateField, order: -1 },
      { field: 'dateCreated', order: 0 }
    ];

    this.taskService.findByFilterSummaryWithDateRange(
      0,
      1000,
      null,
      null,
      multiSortMeta,
      null,
      apiFilters,
      dateRange,
      this.calendarViewMode
    ).pipe(first()).subscribe(
      apiResponse => {
        const loadedTasks = apiResponse.data.tasks || [];

        // Flatten tasks including children and remove duplicates by taskId
        const allTasks: Task[] = [];
        const taskIdSet = new Set<number>();
        
        loadedTasks.forEach(task => {
          // Add parent task if not already added
          if (task.taskId && !taskIdSet.has(task.taskId)) {
            allTasks.push(task);
            taskIdSet.add(task.taskId);
          }
          // Add children if they exist and haven't been added
          if (task.children && task.children.length > 0) {
            task.children.forEach(child => {
              if (child.taskId && !taskIdSet.has(child.taskId)) {
                allTasks.push(child);
                taskIdSet.add(child.taskId);
              }
            });
          }
        });

        // Update tasks array
        this.tasks = allTasks;

        // Group tasks by date
        this.groupTasksByDate();

        // Calculate and emit analytics based on loaded tasks
        this.calculateAnalytics();

        // Emit that filters have been processed
        setTimeout(() => {
          this.filtersProcessed.emit();
        }, 100);

        this.loading = false;
      },
      error => {
        this.alertService.error(error);
        this.loading = false;
        // Still build calendar even if loading fails
        this.buildCalendar();
      }
    );
  }


  getTaskColor(task: Task): string {
    if (!task.dateDue) return '#6c757d';
    
    const dueDate = moment(task.dateDue);
    const today = moment();
    const daysDiff = dueDate.diff(today, 'days');

    if (task.status === 2) { // Completed
      return '#28a745';
    } else if (daysDiff < 0) {
      return '#dc3545'; // Overdue
    } else if (daysDiff === 0) {
      return '#ffc107'; // Due today
    } else if (daysDiff <= 3) {
      return '#fd7e14'; // Due soon
    } else {
      return '#17a2b8'; // Normal
    }
  }

  getPriorityClass(task: Task): string {
    const priority = (task as any).priority;
    switch (priority) {
      case 1: return 'priority-critical';
      case 2: return 'priority-immediate';
      case 3: return 'priority-important';
      case 4: return 'priority-standard';
      case 5: return 'priority-low';
      default: return 'priority-standard';
    }
  }

  onMoreTasksClick(date: moment.Moment, event: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    const dateKey = date.format('YYYY-MM-DD');
    this.expandDate(dateKey);
  }

  /**
   * Handle click on "show less" link
   */
  onShowLessClick(date: moment.Moment, event: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    const dateKey = date.format('YYYY-MM-DD');
    this.collapseDate(dateKey);
  }

  /**
   * Expand a date to show all tasks
   */
  expandDate(dateKey: string): void {
    this.expandedDates.add(dateKey);
  }

  /**
   * Collapse a date to show only 2 tasks
   */
  collapseDate(dateKey: string): void {
    this.expandedDates.delete(dateKey);
  }

  /**
   * Calculate analytics based on currently loaded tasks for the calendar view
   */
  private calculateAnalytics(): void {
    if (!this.tasks || this.tasks.length === 0) {
      this.viewAnalytics = {
        taskForUser: 0,
        taskToday: 0,
        taskThisWeek: 0,
        taskThisMonth: 0,
        taskFollowupToday: 0,
        taskFollowupThisWeek: 0,
        taskFollowupThisMonth: 0
      };
      this.analyticsUpdate.emit(this.viewAnalytics);
      return;
    }

    const today = moment().startOf('day');
    const startOfWeek = moment().startOf('week');
    const endOfWeek = moment().endOf('week');
    const startOfMonth = moment().startOf('month');
    const endOfMonth = moment().endOf('month');

    let taskForUser = 0;
    let taskToday = 0;
    let taskThisWeek = 0;
    let taskThisMonth = 0;
    let taskFollowupToday = 0;
    let taskFollowupThisWeek = 0;
    let taskFollowupThisMonth = 0;

    this.tasks.forEach(task => {
      // Count tasks assigned to current user
      if (task.assignedTo && this.currentUser && task.assignedTo.userId === this.currentUser.userId) {
        taskForUser++;
      }

      // Count tasks due today
      if (task.dateDue) {
        const dueDate = moment(task.dateDue).startOf('day');
        if (dueDate.isSame(today, 'day')) {
          taskToday++;
        }
        if (dueDate.isSameOrAfter(startOfWeek) && dueDate.isSameOrBefore(endOfWeek)) {
          taskThisWeek++;
        }
        if (dueDate.isSameOrAfter(startOfMonth) && dueDate.isSameOrBefore(endOfMonth)) {
          taskThisMonth++;
        }
      }

      // Count followup tasks
      const followupDate = (task as any).dateToFollowup ? moment((task as any).dateToFollowup).startOf('day') : null;
      if (followupDate) {
        if (followupDate.isSame(today, 'day')) {
          taskFollowupToday++;
        }
        if (followupDate.isSameOrAfter(startOfWeek) && followupDate.isSameOrBefore(endOfWeek)) {
          taskFollowupThisWeek++;
        }
        if (followupDate.isSameOrAfter(startOfMonth) && followupDate.isSameOrBefore(endOfMonth)) {
          taskFollowupThisMonth++;
        }
      }
    });

    this.viewAnalytics = {
      taskForUser,
      taskToday,
      taskThisWeek,
      taskThisMonth,
      taskFollowupToday,
      taskFollowupThisWeek,
      taskFollowupThisMonth
    };

    // Emit analytics to parent component
    this.analyticsUpdate.emit(this.viewAnalytics);
  }

  /**
   * Override getTaskSummaryItems to return analytics based on loaded tasks
   */
  getTaskSummaryItems(): any[] {
    return this.taskSummaryConfig.map(item => ({
      ...item,
      value: this.viewAnalytics?.[item.property] || 0
    }));
  }

}

